import useAccess from '@/composables/access/access';
import useGoalAccessPolicyLinks from '@/composables/goal/access-policy-links';
import useGoalProperty from '@/composables/property/goal-property';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import useGoals from '@/composables/goal/goals';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useProperties from '@/composables/property/property';
import { UNASSIGNED } from '@/lib/constants';
import { accessGroupFlag, accessPolicyType, goalProgressMeasurement, goalThresholdTargetArea } from 'shared/constants.json';
import { addKeyResultType, addObjectiveType } from '@/lib/goal/modifier/goal-type';
import { computed, ref } from 'vue';
import { copyAccessPolicy } from '@/lib/access-policy';
import { defaultProgressMeasurement, restrictProgressMeasurement } from '@/lib/goal/modifier/progress-measurement';
import { emptyValues } from '@/lib/property';
import {
  enforceAlignmentRule,
  restrictForeignEntityProperties,
  restrictNonWritableProperties,
} from '@/lib/goal/modifier/restrict-properties';
import { getPropertyValuesFromFilter } from '@/lib/property-value';
import { mergePropertyValues } from '@/lib/goal-grouping';
import { setAlignedItemsGroupedBy } from '@/lib/goal/modifier/aligned-items-group-by';
import { stripEntity, stripGoal, stripPropertyValue } from '@/lib/stripper';

export default function useGoalModifiers(modifiers = ref([])) {
  const goalsSvc = useGoals();
  const { goalTypeProperty, statusProperty, properties: goalProperties } = useGoalProperty();
  const { searchLinkableAccessPolicies, calculateAccessPolicyWithLinks } = useGoalAccessPolicyLinks();
  const { newGoalDefaultAccessPolicy } = useGoalSettings();
  const { userHasRight } = useAccess();
  const hasForeignEntityReference = computed(() => userHasRight([accessGroupFlag.foreignEntityReference]));
  const { spaceProperty } = useProperties();

  const { loggedInUser, spacesWithWriteAccessIds } = useLoggedInUser();
  const { loggedInUserAccount } = useLoggedInUserAccount();

  const usesMultiGoalAlignment = computed(() => loggedInUserAccount.value.accountSettings.usesMultiGoalAlignment);

  let parents = [];
  const setParents = (p) => {
    parents = p;
  };

  let filter = null;
  const setFilter = (f) => {
    filter = f;
  };

  let selectedCycles = null;
  const setSelectedCycles = (s) => {
    selectedCycles = s.filter((c) => c.uid !== UNASSIGNED).map(stripEntity);
  };

  let space = null;
  const setSpace = (o) => {
    space = o;
  };

  const addSpace = (goal) => {
    if (space === null) {
      return goal;
    }
    goal.properties = mergePropertyValues([{
      property: goalProperties.value.find((p) => p.uid === space.property.uid),
      spaces: [space],
    }], goal.properties);
    return goal;
  };

  let option = null;
  const setOption = (o) => {
    option = o;
  };

  const addOption = (goal) => {
    if (option === null) {
      return goal;
    }
    goal.properties = mergePropertyValues([{
      property: goalProperties.value.find((p) => p.uid === option.property.uid),
      selectedOptions: [option],
    }], goal.properties);
    return goal;
  };

  const parentsFromFilter = (goal) => {
    const { parents: filterParents } = getPropertyValuesFromFilter(filter, []);
    if (typeof filterParents === 'undefined') {
      return goal;
    }
    goal.parents = [...goal.parents, ...filterParents];
    return goal;
  };

  const parentsFromParams = (goal) => {
    if (!Array.isArray(parents)) {
      return goal;
    }
    goal.parents = [...goal.parents, ...parents];
    return goal;
  };

  const fixParents = (goal) => {
    if (usesMultiGoalAlignment.value || goal.parents.length <= 1) {
      return goal;
    }

    goal.parents = [goal.parents[0]];
    return goal;
  };

  const parentsModifiers = (goal) => {
    if (duplicate !== null) {
      return applyModifiers([
        fixParents,
      ], goal);
    }

    return applyModifiers([
      parentsFromFilter,
      parentsFromParams,
      fixParents,
    ], goal);
  };

  const paysOntoFromParents = (goal) => {
    if (goal.progressMeasurement === goalProgressMeasurement.none) {
      return goal;
    }
    let goalParents = goal.parents;
    if (duplicate !== null) {
      goalParents = goalsSvc.selectMultiple(goal.parents.map((p) => p.uid));
    }
    goal.paysOnto = goalParents.filter((p) => [accessPolicyType.write, accessPolicyType.full].includes(p.accessRight)).map((g) => ({ uid: g.uid }));
    return goal;
  };

  const propertiesFromParents = (goal) => {
    goal.parents.forEach((p) => {
      goal.properties = mergePropertyValues(p.properties, goal.properties);
    });
    return goal;
  };

  const valuesFromFilter = (goal) => {
    if (goal.parents.length > 0) {
      return propertiesFromParents(goal);
    }

    const { properties } = getPropertyValuesFromFilter(filter, goal.properties);
    goal.properties = properties;
    return goal;
  };

  const propertiesModifiers = (goal) => {
    if (duplicate !== null) {
      return applyModifiers([
        restrictNonWritableProperties([statusProperty.value]),
        restrictForeignEntityProperties({ hasForeignEntityReference, spaceProperty: spaceProperty.value, nativeTeamsIds: spacesWithWriteAccessIds.value }),
      ], goal);
    }

    return applyModifiers([
      valuesFromFilter,
      addKeyResultType(goalProperties),
      addObjectiveType(goalProperties),
      addSpace,
      addOption,
      restrictNonWritableProperties([statusProperty.value]),
      restrictForeignEntityProperties({ hasForeignEntityReference, spaceProperty: spaceProperty.value, nativeTeamsIds: spacesWithWriteAccessIds.value }),
      enforceAlignmentRule(goalTypeProperty.value, goalsSvc.selectMultiple),
    ], goal);
  };

  const progressMeasurementModifiers = (goal) => {
    if (duplicate !== null) {
      return goal;
    }

    return applyModifiers([
      defaultProgressMeasurement,
      restrictProgressMeasurement(goalTypeProperty.value),
    ], goal);
  };

  const setGoalCycles = (goal) => {
    if (selectedCycles !== null) {
      goal.goalCycle = selectedCycles;
      return goal;
    }

    if (duplicate !== null) {
      return goal;
    }

    if (goal.parents.length > 0) {
      goal.goalCycle = goal.parents[0].goalCycle;
      return goal;
    }

    return goal;
  };

  const setAccessPolicy = (goal) => {
    if (duplicate !== null) {
      return goal;
    }
    if (goal.parents.length > 0) {
      goal.accessPolicy = copyAccessPolicy(goal.parents[0].accessPolicy);
      return goal;
    }
    const linkableAPs = searchLinkableAccessPolicies(goal);
    if (linkableAPs.length > 0) {
      goal.accessPolicy = calculateAccessPolicyWithLinks({ ...goal, accessPolicy: { account: loggedInUserAccount.value, accountAccess: accessPolicyType.disabled } });
      return goal;
    }
    goal.accessPolicy = newGoalDefaultAccessPolicy.value;
    return goal;
  };

  let duplicate = null;
  const setDuplicate = (goal) => {
    duplicate = goal;
  };
  const defaultModifier = () => {
    if (duplicate !== null) {
      return stripGoal({
        ...duplicate,
        uid: undefined,
        rid: duplicate.uid,
        account: { uid: loggedInUserAccount.value.uid },
        creator: { uid: loggedInUser.value.uid },
        accessPolicy: copyAccessPolicy(duplicate.accessPolicy),
        subscriptions: [{ active: true, user: { uid: loggedInUser.value.uid } }],
        parents: duplicate.parents,
        paysOnto: duplicate.paysOnto,
        publishedAt: duplicate.publishedAt ? (new Date()).toISOString() : null,
        properties: duplicate.properties.map(stripPropertyValue),
      });
    }

    return {
      account: { uid: loggedInUserAccount.value.uid },
      creator: { uid: loggedInUser.value.uid },
      parents: [],
      paysOnto: [],
      start: 0,
      threshold: 0,
      thresholdTargetArea: goalThresholdTargetArea.above,
      end: 100,
      metric: '%',
      publishedAt: (new Date()).toISOString(),
      properties: emptyValues(goalProperties.value),
    };
  };

  const getModifiers = () => [
    defaultModifier,
    parentsModifiers,
    propertiesModifiers,
    progressMeasurementModifiers,
    paysOntoFromParents,
    setGoalCycles,
    setAlignedItemsGroupedBy(goalProperties),
    ...modifiers.value,
    setAccessPolicy,
  ];

  const applyModifiers = (modifiers, target = {}) => modifiers.reduce((res, m) => {
    res = m(res);
    return res;
  }, target);

  return {
    applyModifiers,
    getModifiers,
    setParents,
    setFilter,
    setSelectedCycles,
    setOption,
    setSpace,
    setDuplicate,
  };
}
